import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  ArrowLeft, Share2, Download, QrCode, User, Calendar, 
  Droplets, AlertTriangle, Pill, Heart, Stethoscope, 
  Shield, Phone, Copy, Check, X, CloudOff, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { haptics } from '@/utils/haptics';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import useOfflineFirst from '@/hooks/useOfflineFirst';
import useSensitiveAction from '@/hooks/useSensitiveAction';
import SyncStatusIndicator from '@/components/SyncStatusIndicator';
import VerifyIdentityDialog from '@/components/VerifyIdentityDialog';
import { getSyncStatus } from '@/services/offlineDataService';
import { useDemo } from '@/contexts/DemoContext';

/**
 * EmergencySummaryPage - Shareable one-page medical summary
 * 
 * Now reads from shared medical data (single source of truth)
 * Changes made here or in Medical section are reflected everywhere
 * 
 * Features:
 * - Clean, readable medical summary
 * - Share functionality
 * - PDF export
 * - QR code generation
 */

const EmergencySummaryPage = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const summaryRef = useRef(null);
  const { isDemoMode, getProfiles, getMedicalProfile, getAllergies, getMedications, getConditions, getDoctors, getInsurance, getEmergencyContacts } = useDemo();
  
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [accessToken, setAccessToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [fromCache, setFromCache] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // Offline-first data management
  const { fetchWithCache } = useOfflineFirst('emergencyBinder');
  
  // Sensitive action protection
  const { executeSensitiveAction, willRequireVerification, isVerifying } = useSensitiveAction();

  useEffect(() => {
    fetchData();
  }, [profileId, isDemoMode]);

  // Track pending sync changes
  useEffect(() => {
    const checkPendingChanges = () => {
      const status = getSyncStatus();
      setHasPendingChanges(status.hasPendingChanges);
    };
    
    checkPendingChanges();
    const interval = setInterval(checkPendingChanges, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Demo mode: build summary from local demo data
      if (isDemoMode) {
        const profiles = getProfiles() || [];
        const demoProfile = profiles.find(p => p.id === profileId) || profiles[0];
        const medicalProfile = getMedicalProfile() || {};
        const allergies = getAllergies() || [];
        const medications = getMedications() || [];
        const conditions = getConditions() || [];
        const doctors = getDoctors() || [];
        const insurance = getInsurance() || [];
        const emergencyContacts = getEmergencyContacts() || [];
        const primaryDoctor = doctors[0];
        const primaryInsurance = insurance[0];
        const primaryEmergencyContact = emergencyContacts[0];
        
        const demoSummary = {
          profile_name: demoProfile?.name || medicalProfile.full_name || 'Demo User',
          date_of_birth: medicalProfile.date_of_birth || '',
          blood_type: medicalProfile.blood_type || '',
          allergies: allergies.map(a => a.name),
          medications: medications.map(m => `${m.name}${m.dosage ? ` ${m.dosage}` : ''}${m.frequency ? ` - ${m.frequency}` : ''}`),
          conditions: conditions.map(c => c.condition || c.name),
          primary_doctor: primaryDoctor?.name || '',
          primary_doctor_phone: primaryDoctor?.phone || '',
          insurance_provider: primaryInsurance?.provider || '',
          insurance_policy_number: primaryInsurance?.policy_number || '',
          emergency_contact: primaryEmergencyContact?.name || '',
          emergency_contact_relationship: primaryEmergencyContact?.relationship || '',
          emergency_contact_phone: primaryEmergencyContact?.phone || ''
        };
        
        setSummary(demoSummary);
        setProfile({ name: demoSummary.profile_name });
        setFromCache(false);
        
        // Generate a demo QR code
        try {
          const demoShareUrl = `${window.location.origin}/emergency-view/demo-access-token`;
          const qrUrl = await QRCode.toDataURL(demoShareUrl, {
            width: 200,
            margin: 2,
            color: { dark: '#1e293b', light: '#ffffff' }
          });
          setQrCodeUrl(qrUrl);
          setAccessToken('demo-access-token');
        } catch (e) {
          console.log('QR generation skipped');
        }
        
        setLoading(false);
        return;
      }
      
      // Use offline-first fetching for emergency summary
      const result = await fetchWithCache('/shared-medical/emergency-summary', { idKey: 'id' });
      
      setSummary(result.data);
      setProfile({ name: result.data.profile_name });
      setFromCache(result.fromCache || false);
      
      if (result.fromCache) {
        toast.info('Showing cached data', { 
          description: 'You may be offline. Data will sync when connected.',
          duration: 3000
        });
      }
      
      // Try to get access token if online
      if (profileId && navigator.onLine) {
        try {
          const tokenRes = await API.get(`/emergency-binder/profiles/${profileId}/access-token`);
          if (tokenRes.data.access_token) {
            setAccessToken(tokenRes.data.access_token);
            const shareUrl = `${window.location.origin}/emergency-view/${tokenRes.data.access_token}`;
            const qrUrl = await QRCode.toDataURL(shareUrl, {
              width: 200,
              margin: 2,
              color: { dark: '#1e293b', light: '#ffffff' }
            });
            setQrCodeUrl(qrUrl);
          }
        } catch (e) {
          // Token fetch failed, not critical
          console.log('Token fetch skipped');
        }
      }
    } catch (error) {
      console.error('Failed to fetch emergency data:', error);
      if (!isDemoMode) {
        if (!navigator.onLine) {
          toast.error('You are offline and no cached data is available');
        } else {
          toast.error('Failed to load emergency summary');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate access token for QR code sharing
  const generateAccessToken = async () => {
    try {
      setGeneratingToken(true);
      const response = await API.post(`/emergency-binder/profiles/${profileId}/generate-access-token`);
      const token = response.data.access_token;
      setAccessToken(token);
      
      // Generate QR code
      const shareUrl = `${window.location.origin}/emergency-view/${token}`;
      const qrUrl = await QRCode.toDataURL(shareUrl, {
        width: 200,
        margin: 2,
        color: { dark: '#1e293b', light: '#ffffff' }
      });
      setQrCodeUrl(qrUrl);
      setShowQR(true);
      toast.success('QR code generated successfully');
    } catch (error) {
      console.error('Failed to generate access token:', error);
      toast.error('Failed to generate QR code');
    } finally {
      setGeneratingToken(false);
    }
  };

  // Show QR code or generate if needed
  const handleShowQR = async () => {
    haptics.tap();
    
    // Wrap in sensitive action protection
    const performAction = async () => {
      if (accessToken && qrCodeUrl) {
        setShowQR(true);
      } else {
        await generateAccessToken();
      }
    };
    
    await executeSensitiveAction(performAction, { actionName: 'view QR code' });
  };

  // Share functionality
  const handleShare = async () => {
    haptics.tap();
    
    // Wrap in sensitive action protection
    const performShare = async () => {
      const shareData = {
        title: `Emergency Info - ${summary?.profile_name || profile?.name}`,
        text: generateShareText(),
        url: `${window.location.origin}/emergency-view/${profileId}`
      };

      if (navigator.share) {
        try {
          await navigator.share(shareData);
          toast.success('Shared successfully');
        } catch (err) {
          if (err.name !== 'AbortError') {
            setShowShareMenu(true);
          }
        }
      } else {
        setShowShareMenu(true);
      }
    };
    
    await executeSensitiveAction(performShare, { actionName: 'share emergency info' });
  };

  // Generate text for sharing
  const generateShareText = () => {
    const lines = [
      `EMERGENCY MEDICAL INFORMATION`,
      `Name: ${summary?.profile_name || profile?.name || 'N/A'}`,
      summary?.date_of_birth ? `DOB: ${summary.date_of_birth}` : '',
      summary?.blood_type ? `Blood Type: ${summary.blood_type}` : '',
      '',
      `ALLERGIES: ${summary?.allergies?.join(', ') || 'None known'}`,
      '',
      `MEDICATIONS: ${summary?.medications?.join(', ') || 'None'}`,
      '',
      summary?.conditions?.length > 0 ? `CONDITIONS: ${summary.conditions.join(', ')}` : '',
      '',
      summary?.primary_doctor ? `Doctor: ${summary.primary_doctor}` : '',
      summary?.primary_doctor_phone ? `Doctor Phone: ${summary.primary_doctor_phone}` : '',
      '',
      summary?.emergency_contact ? `Emergency Contact: ${summary.emergency_contact}` : '',
      summary?.emergency_contact_phone ? `Phone: ${summary.emergency_contact_phone}` : '',
    ].filter(Boolean);
    
    return lines.join('\n');
  };

  // Copy to clipboard
  const handleCopy = async () => {
    haptics.tap();
    
    const performCopy = async () => {
      try {
        await navigator.clipboard.writeText(generateShareText());
        setCopied(true);
        toast.success('Copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        toast.error('Failed to copy');
      }
    };
    
    await executeSensitiveAction(performCopy, { actionName: 'copy emergency info' });
  };

  // Export as PDF
  const handleExportPDF = async () => {
    haptics.press();
    
    const performExport = () => {
      const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    let y = 20;
    
    // Header
    pdf.setFillColor(239, 68, 68);
    pdf.rect(0, 0, pageWidth, 35, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(22);
    pdf.setFont('helvetica', 'bold');
    pdf.text('EMERGENCY MEDICAL INFORMATION', pageWidth / 2, 15, { align: 'center' });
    pdf.setFontSize(12);
    pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 28, { align: 'center' });
    
    y = 50;
    pdf.setTextColor(30, 41, 59);
    
    // Name and DOB
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text(summary?.profile_name || profile?.name || 'Unknown', 20, y);
    y += 10;
    
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    if (summary?.date_of_birth) {
      pdf.text(`Date of Birth: ${summary.date_of_birth}`, 20, y);
      y += 7;
    }
    if (summary?.blood_type) {
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Blood Type: ${summary.blood_type}`, 20, y);
      y += 7;
    }
    
    y += 10;
    
    // Allergies
    pdf.setFillColor(251, 191, 36);
    pdf.rect(15, y - 5, pageWidth - 30, 8, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('ALLERGIES', 20, y);
    y += 10;
    pdf.setTextColor(30, 41, 59);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    if (summary?.allergies?.length > 0) {
      summary.allergies.forEach(allergy => {
        pdf.text(`• ${allergy}`, 25, y);
        y += 7;
      });
    } else {
      pdf.text('No known allergies', 25, y);
      y += 7;
    }
    
    y += 8;
    
    // Medications
    pdf.setFillColor(59, 130, 246);
    pdf.rect(15, y - 5, pageWidth - 30, 8, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('MEDICATIONS', 20, y);
    y += 10;
    pdf.setTextColor(30, 41, 59);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    if (summary?.medications?.length > 0) {
      summary.medications.forEach(med => {
        pdf.text(`• ${med}`, 25, y);
        y += 7;
      });
    } else {
      pdf.text('No current medications', 25, y);
      y += 7;
    }
    
    y += 8;
    
    // Medical Conditions
    if (summary?.conditions?.length > 0) {
      pdf.setFillColor(147, 51, 234);
      pdf.rect(15, y - 5, pageWidth - 30, 8, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('MEDICAL CONDITIONS', 20, y);
      y += 10;
      pdf.setTextColor(30, 41, 59);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      summary.conditions.forEach(condition => {
        pdf.text(`• ${condition}`, 25, y);
        y += 7;
      });
      y += 8;
    }
    
    // Doctor
    if (summary?.primary_doctor) {
      pdf.setFillColor(100, 116, 139);
      pdf.rect(15, y - 5, pageWidth - 30, 8, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('PRIMARY DOCTOR', 20, y);
      y += 10;
      pdf.setTextColor(30, 41, 59);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(summary.primary_doctor, 25, y);
      y += 7;
      if (summary.primary_doctor_phone) {
        pdf.text(`Phone: ${summary.primary_doctor_phone}`, 25, y);
        y += 7;
      }
      y += 8;
    }
    
    // Emergency Contact
    if (summary?.emergency_contact) {
      pdf.setFillColor(16, 185, 129);
      pdf.rect(15, y - 5, pageWidth - 30, 8, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('EMERGENCY CONTACT', 20, y);
      y += 10;
      pdf.setTextColor(30, 41, 59);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text(summary.emergency_contact, 25, y);
      y += 7;
      pdf.setFont('helvetica', 'normal');
      if (summary.emergency_contact_relationship) {
        pdf.text(`(${summary.emergency_contact_relationship})`, 25, y);
        y += 7;
      }
      if (summary.emergency_contact_phone) {
        pdf.setFont('helvetica', 'bold');
        pdf.text(summary.emergency_contact_phone, 25, y);
      }
    }
    
    // Insurance
    if (summary?.insurance_provider) {
      y += 15;
      pdf.setFillColor(236, 72, 153);
      pdf.rect(15, y - 5, pageWidth - 30, 8, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('INSURANCE', 20, y);
      y += 10;
      pdf.setTextColor(30, 41, 59);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(summary.insurance_provider, 25, y);
      y += 7;
      if (summary.insurance_policy_number) {
        pdf.text(`Policy #: ${summary.insurance_policy_number}`, 25, y);
      }
    }
    
    // Save
    const fileName = `emergency-summary-${(summary?.profile_name || profile?.name || 'unknown').toLowerCase().replace(/\s+/g, '-')}.pdf`;
    pdf.save(fileName);
    toast.success('PDF exported successfully');
    };
    
    await executeSensitiveAction(performExport, { actionName: 'export PDF' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  const medicalInfo = profile?.medical_info || {};

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="emergency-summary-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-4 py-3 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-slate-600"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="text-center">
            <h1 className="text-lg font-semibold text-slate-800">Emergency Summary</h1>
            {fromCache && (
              <p className="text-xs text-amber-600 flex items-center justify-center gap-1">
                <CloudOff className="h-3 w-3" />
                Offline mode
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleShare}
            className="text-blue-500"
            disabled={fromCache}
          >
            <Share2 className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {/* Sync Status Banner - shows when there are pending changes or offline */}
      {(hasPendingChanges || fromCache) && (
        <div className="px-4 pt-4">
          <SyncStatusIndicator variant="full" size="md" />
        </div>
      )}

      <main className="px-4 py-6 space-y-4" ref={summaryRef}>
        {/* Profile Card */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-rose-50 to-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center">
                <User className="h-8 w-8 text-rose-500" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800">
                  {summary?.profile_name || profile?.name}
                </h2>
                {summary?.date_of_birth && (
                  <p className="text-slate-500 flex items-center gap-1.5 mt-1">
                    <Calendar className="h-4 w-4" />
                    DOB: {summary.date_of_birth}
                  </p>
                )}
                {summary?.blood_type && (
                  <p className="text-rose-600 font-semibold flex items-center gap-1.5 mt-1">
                    <Droplets className="h-4 w-4" />
                    Blood Type: {summary.blood_type}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Allergies */}
        <Card className="border-0 shadow-sm border-l-4 border-l-amber-400">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <h3 className="font-semibold text-slate-800">Allergies</h3>
            </div>
            {summary?.allergies?.length > 0 ? (
              <ul className="space-y-1.5">
                {summary.allergies.map((allergy, i) => (
                  <li key={i} className="text-slate-700 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-amber-400 rounded-full" />
                    {allergy}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-500">No known allergies</p>
            )}
          </CardContent>
        </Card>

        {/* Medications */}
        <Card className="border-0 shadow-sm border-l-4 border-l-blue-400">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Pill className="h-5 w-5 text-blue-500" />
              <h3 className="font-semibold text-slate-800">Medications</h3>
            </div>
            {summary?.medications?.length > 0 ? (
              <ul className="space-y-1.5">
                {summary.medications.map((med, i) => (
                  <li key={i} className="text-slate-700 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                    {med}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-500">No current medications</p>
            )}
          </CardContent>
        </Card>

        {/* Medical Conditions */}
        {summary?.conditions?.length > 0 && (
          <Card className="border-0 shadow-sm border-l-4 border-l-purple-400">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Heart className="h-5 w-5 text-purple-500" />
                <h3 className="font-semibold text-slate-800">Medical Conditions</h3>
              </div>
              <ul className="space-y-1.5">
                {summary.conditions.map((condition, i) => (
                  <li key={i} className="text-slate-700 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-purple-400 rounded-full" />
                    {condition}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Primary Doctor */}
        {summary?.primary_doctor && (
          <Card className="border-0 shadow-sm border-l-4 border-l-slate-400">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Stethoscope className="h-5 w-5 text-slate-500" />
                <h3 className="font-semibold text-slate-800">Primary Doctor</h3>
              </div>
              <p className="text-slate-700 font-medium">{summary.primary_doctor}</p>
              {summary.primary_doctor_phone && (
                <a 
                  href={`tel:${summary.primary_doctor_phone}`}
                  className="text-blue-500 hover:underline mt-1 block"
                >
                  {summary.primary_doctor_phone}
                </a>
              )}
            </CardContent>
          </Card>
        )}

        {/* Insurance */}
        {summary?.insurance_provider && (
          <Card className="border-0 shadow-sm border-l-4 border-l-pink-400">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="h-5 w-5 text-pink-500" />
                <h3 className="font-semibold text-slate-800">Insurance</h3>
              </div>
              <p className="text-slate-700 font-medium">{summary.insurance_provider}</p>
              {summary.insurance_policy_number && (
                <p className="text-slate-500">Policy #: {summary.insurance_policy_number}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Emergency Contact */}
        {summary?.emergency_contact && (
          <Card className="border-0 shadow-sm border-l-4 border-l-emerald-400">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Phone className="h-5 w-5 text-emerald-500" />
                <h3 className="font-semibold text-slate-800">Emergency Contact</h3>
              </div>
              <p className="text-slate-700 font-medium">{summary.emergency_contact}</p>
              {summary.emergency_contact_relationship && (
                <p className="text-slate-500 text-sm">({summary.emergency_contact_relationship})</p>
              )}
              {summary.emergency_contact_phone && (
                <a 
                  href={`tel:${summary.emergency_contact_phone}`}
                  className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-emerald-500 text-white rounded-lg font-medium"
                >
                  <Phone className="h-4 w-4" />
                  {summary.emergency_contact_phone}
                </a>
              )}
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-3 gap-3 pt-4">
          <Button
            variant="outline"
            onClick={handleShare}
            className="flex flex-col items-center gap-1.5 h-auto py-4"
          >
            <Share2 className="h-5 w-5 text-blue-500" />
            <span className="text-xs">Share</span>
          </Button>
          <Button
            variant="outline"
            onClick={handleExportPDF}
            className="flex flex-col items-center gap-1.5 h-auto py-4"
          >
            <Download className="h-5 w-5 text-green-500" />
            <span className="text-xs">Export PDF</span>
          </Button>
          <Button
            variant="outline"
            onClick={handleShowQR}
            disabled={generatingToken}
            className="flex flex-col items-center gap-1.5 h-auto py-4"
          >
            {generatingToken ? (
              <div className="h-5 w-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <QrCode className="h-5 w-5 text-purple-500" />
            )}
            <span className="text-xs">{generatingToken ? 'Generating...' : 'QR Code'}</span>
          </Button>
        </div>
      </main>

      {/* QR Code Modal */}
      {showQR && qrCodeUrl && (
        <div 
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowQR(false)}
        >
          <Card 
            className="w-full max-w-sm relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* X Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 text-slate-400 hover:text-slate-600 z-10"
              onClick={() => setShowQR(false)}
            >
              <X className="h-5 w-5" />
            </Button>
            <CardContent className="p-6 text-center">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">
                Emergency QR Code
              </h3>
              <div className="bg-white p-4 rounded-xl inline-block mb-4 border border-slate-200">
                <img src={qrCodeUrl} alt="QR Code" className="w-48 h-48" />
              </div>
              <p className="text-sm text-slate-500 mb-2">
                Scan this code to view emergency information
              </p>
              <p className="text-xs text-slate-400 mb-4">
                Anyone with this QR code can view medical info without logging in
              </p>
              <div className="space-y-2">
                <Button
                  onClick={async () => {
                    const shareUrl = `${window.location.origin}/emergency-view/${accessToken}`;
                    await navigator.clipboard.writeText(shareUrl);
                    toast.success('Link copied to clipboard');
                  }}
                  variant="outline"
                  className="w-full"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Share Link
                </Button>
                <Button
                  onClick={() => setShowQR(false)}
                  className="w-full"
                >
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Share Menu Modal */}
      {showShareMenu && (
        <div 
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end justify-center"
          onClick={() => setShowShareMenu(false)}
        >
          <Card 
            className="w-full max-w-lg rounded-t-3xl rounded-b-none relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* X Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 z-10"
              onClick={() => setShowShareMenu(false)}
            >
              <X className="h-5 w-5" />
            </Button>
            <CardContent className="p-6">
              <div className="flex justify-center mb-4">
                <div className="w-10 h-1 bg-slate-300 rounded-full" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-4 text-center">
                Share Emergency Summary
              </h3>
              <div className="space-y-3">
                <Button
                  variant="outline"
                  onClick={handleCopy}
                  className="w-full justify-start gap-3 h-12"
                >
                  {copied ? <Check className="h-5 w-5 text-green-500" /> : <Copy className="h-5 w-5" />}
                  {copied ? 'Copied!' : 'Copy to Clipboard'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleExportPDF}
                  className="w-full justify-start gap-3 h-12"
                >
                  <Download className="h-5 w-5" />
                  Export as PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowShareMenu(false);
                    setShowQR(true);
                  }}
                  className="w-full justify-start gap-3 h-12"
                >
                  <QrCode className="h-5 w-5" />
                  Show QR Code
                </Button>
              </div>
              <Button
                variant="ghost"
                onClick={() => setShowShareMenu(false)}
                className="w-full mt-4"
              >
                Cancel
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default EmergencySummaryPage;
